/*
SPDX-FileCopyrightText: 2024 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
import { useState, useRef } from "react";
import { deepCopy, generateId } from "../general/utils";
import { httpClient } from "../services/http/httpClient";
import { generateFilter, resetAllFilters } from "../filtering/utils";
import { useEffectUpdate } from "../hooks";
import { getUserFromLocalStorage } from "../services/localStorage/localStorageService";
import { BOARD_URL_PREFIX, BOARD_ENDPOINTS, BoardObjectTypes, } from "../constants/api.constants";
/*
example layout of a board:

export const exampleBoard: Board = {
  views: {
    'viewIdOne': {
      zones: {
        'zoneIdOne': {
          components: {
            'componentIdOne': {
              data: {
                filter: {
                  and_: {
                    'attributeId': {
                      eq: {
                        value: 'hello',
                        negate: true
                      }
                    }
                  }
                },
                defaultFilter: {
                  and_: {
                    'attributeId': {
                      eq: {
                        value: 'hello',
                        negate: true
                      }
                    }
                  }
                }
              },
            }
          },
          order: ['componentIdOne'],
          type: 'species'
        }
      },
      order: ['zoneIdOne']
    }
  },
  order: ['viewIdOne']
};
*/
export function defineComponent(component, zone) {
    // setting default as empty if no filter provided
    const f = component.filter === undefined ? { and_: {} } : component.filter;
    zone.components[component.id] = {
        data: {
            filter: deepCopy(f),
            defaultFilter: deepCopy(f),
            ...component,
        },
    };
}
export function defineZone(objectType, components, filter) {
    const f = filter === undefined ? { and_: {} } : filter;
    const zone = {
        components: {},
        order: [],
        type: objectType,
        filter: deepCopy(f),
        defaultFilter: deepCopy(f),
    };
    for (const component of components) {
        defineComponent(component, zone);
        zone.order.push(component.id);
    }
    return zone;
}
export function useZone(params) {
    const { endpoint, baseUrl, components, filter } = params;
    const [zone, setZone] = useState(defineZone(endpoint, components, filter));
    return {
        endpoint: endpoint,
        baseUrl: baseUrl,
        zone: zone,
        setZone: setZone,
    };
}
export function generateTranslatedFilter(source, translations, excludeAfterId) {
    const sourceFilter = generateFilter(source.zone, excludeAfterId, true);
    const translatedFilter = { and_: {} };
    Object.entries(translations).map(([sourceAttribute, targetAttribute]) => {
        if (sourceAttribute in sourceFilter.and_) {
            translatedFilter.and_[targetAttribute] =
                sourceFilter.and_[sourceAttribute];
        }
    });
    return translatedFilter;
}
export function useTranslator(params) {
    const { source, target, translations, defaultFilter, excludeAfterId } = params;
    const prevFilter = useRef(defaultFilter ? defaultFilter : { and_: {} });
    useEffectUpdate(() => {
        const translatedFilter = generateTranslatedFilter(source, translations, excludeAfterId);
        if (JSON.stringify(translatedFilter) !== JSON.stringify(prevFilter.current)) {
            resetAllFilters(target.zone);
            target.zone.filter = translatedFilter;
            target.setZone({ ...target.zone });
            prevFilter.current = translatedFilter;
        }
    }, [source.zone]);
}
export function getWidgetOrder(layout) {
    // Sort the layout array by the 'y' property (and 'x' property in case of a tie)
    layout.sort((a, b) => a.y - b.y || a.x - b.x);
    // Map the sorted layout array to an array of widget objects
    const widgetOrder = layout.map((item) => item.i);
    return {
        order: widgetOrder,
    };
}
export async function getBoard(id, ds, user) {
    const res = await ds
        .getOne({
        objectType: BOARD_ENDPOINTS.BOARD,
        id: id,
        user_id: user.id,
    })
        .then(async (res) => {
        const views = await getViews(res.id, ds);
        return {
            boardTitle: res.title,
            boardFilter: res.filter,
            views: views,
        };
    });
    return res;
}
async function getViews(id, ds) {
    return await httpClient()
        .get(`/${BOARD_ENDPOINTS.BOARD_VIEWS}`, {
        params: {
            filter: {
                and_: {
                    "board.id": { eq: { value: id } },
                },
            },
        },
    })
        .then((res) => {
        const ids = res.data.data.map((view) => view.relationships.view.data.id); // Fix Proxy
        return getViewsData(ids, ds);
    });
}
async function getViewsData(ids, ds) {
    return await ds
        .getListPage({
        objectType: BOARD_ENDPOINTS.VIEW,
        filter: {
            and_: {
                id: { in_list: { value: ids } },
            },
        },
    })
        .then((res) => {
        return res;
    });
}
export async function getZones(viewID, ds) {
    return await httpClient()
        .get(`/${BOARD_ENDPOINTS.VIEW_ZONES}`, {
        params: {
            filter: {
                and_: {
                    view_id: { eq: { value: viewID } },
                },
            },
        },
    })
        .then(async (res) => {
        // Removes duplicate values
        const ids = Array.from(new Set(res.data.data.map((zone) => zone.relationships.zone.data.id)));
        const zoneData = await getZoneData(ids, ds);
        return {
            order: formatZoneOrders(res.data.data),
            zones: zoneData,
        };
    });
}
function formatZoneOrders(data) {
    const formattedData = data.map((zone) => {
        return {
            zoneId: zone.relationships.zone.data.id,
            order: zone.attributes.order,
            zoneViewId: zone.id,
        };
    });
    return formattedData;
}
async function getZoneData(ids, ds) {
    return await ds
        .getListPage({
        objectType: BOARD_ENDPOINTS.ZONE,
        filter: {
            and_: {
                id: { in_list: { value: ids } },
            },
        },
    })
        .then((res) => {
        return res;
    });
}
export function saveTitle(title, ds, id, objectType) {
    ds.upsert({
        objectType: `${BOARD_URL_PREFIX}/${objectType}`,
        payload: [
            {
                type: objectType,
                id: id,
                attributes: {
                    title: title,
                },
            },
        ],
    });
}
export async function getComponents(zoneId, ds) {
    const componentZoneData = await getComponentZoneData(zoneId);
    // @ts-ignore
    const componentIds = componentZoneData.data.data.map((component) => component.relationships.component.data.id);
    const componentData = await getComponentData(componentIds, ds);
    // @ts-ignore
    return componentZoneData.data.data.map((component) => {
        const componentId = component.relationships.component.data.id;
        const componentDetails = componentData.find((data) => data.id === componentId);
        return {
            componentId: componentId,
            order: component.attributes.order,
            componentZoneId: component.id,
            componentType: componentDetails.component_type,
            filter: componentDetails.filter,
            title: componentDetails.title,
            objectType: componentDetails.object_type,
            baseUrl: componentDetails.base_url,
            config: componentDetails.config,
            widgetType: componentDetails.widget_type,
            filterPassThrough: componentDetails.filter_pass_through,
        };
    });
}
async function getComponentZoneData(zoneId) {
    return await httpClient().get(`/${BOARD_ENDPOINTS.ZONE_COMPONENTS}`, {
        params: {
            filter: {
                and_: {
                    zone_id: { eq: { value: zoneId } },
                },
            },
        },
    });
}
async function getComponentData(componentIds, ds) {
    return await ds.getListPage({
        objectType: BOARD_ENDPOINTS.COMPONENT,
        filter: {
            and_: {
                id: { in_list: { value: componentIds } },
            },
        },
    });
}
export const generateLayout = (components) => {
    // Left hand side are the component types, right are the breakpoints
    const types = {
        sm: { lg: { w: 1, h: 1 }, md: { w: 1, h: 1 }, sm: { w: 1, h: 1 } },
        md: { lg: { w: 2, h: 3 }, md: { w: 2, h: 3 }, sm: { w: 1, h: 3 } },
        lg: { lg: { w: 4, h: 4 }, md: { w: 2, h: 4 }, sm: { w: 1, h: 4 } },
    };
    const layout = { lg: [], md: [], sm: [] };
    const y = { lg: 0, md: 0, sm: 0 };
    const x = { lg: 0, md: 0, sm: 0 };
    components.forEach((component) => {
        const type = component.widgetType || "sm";
        ["lg", "md", "sm"].forEach((breakpoint) => {
            const { w, h } = types[type][breakpoint];
            // if the widget won't fit on the current row, move it to the next row
            if (x[breakpoint] + w >
                (breakpoint === "lg" ? 4 : breakpoint === "md" ? 2 : 1)) {
                y[breakpoint] += h;
                x[breakpoint] = 0;
            }
            layout[breakpoint].push({
                i: component.componentId,
                x: x[breakpoint],
                y: y[breakpoint],
                w,
                h,
            });
            x[breakpoint] += w;
        });
    });
    return layout;
};
export async function createBoardAndView(ds, id, title, viewId, viewTitle) {
    const user = getUserFromLocalStorage();
    const boardId = id ?? generateId("b");
    await ds
        .upsert({
        objectType: BOARD_ENDPOINTS.BOARD,
        payload: [
            {
                type: BoardObjectTypes.BOARD,
                id: boardId,
                attributes: {
                    title: title,
                    filter: { and_: {} },
                    user_id: user.id,
                },
            },
        ],
    })
        .then(async () => {
        return addView(ds, viewId, viewTitle);
    })
        .then(async () => {
        await ds
            .upsert({
            objectType: BOARD_ENDPOINTS.BOARD_VIEWS,
            payload: [
                {
                    type: BoardObjectTypes.VIEW_BOARD,
                    attributes: {
                        order: 1,
                        board_id: boardId,
                        view_id: viewId,
                    },
                },
            ],
        })
            .catch((err) => {
            console.error(err);
        });
    });
}
//@ts-ignore
export async function addView(ds, id, title) {
    const user = getUserFromLocalStorage();
    const viewId = id ?? generateId("v");
    await ds
        .upsert({
        objectType: BOARD_ENDPOINTS.VIEW,
        payload: [
            {
                type: BoardObjectTypes.VIEW,
                id: viewId,
                attributes: {
                    title: "View 1",
                    filter: { and_: {} },
                    user_id: user.id,
                },
            },
        ],
    })
        .catch((err) => {
        console.error(err);
    });
}
export async function addZone(ds, objectType, title, nextOrder, viewId, dataUrl) {
    const user = getUserFromLocalStorage();
    const newId = generateId("z");
    await ds.upsert({
        objectType: BOARD_ENDPOINTS.ZONE,
        payload: [
            {
                type: BoardObjectTypes.ZONE,
                id: newId,
                attributes: {
                    title: title,
                    filter: { and_: {} },
                    object_type: objectType,
                    user_id: user.id,
                    base_url: dataUrl,
                },
            },
        ],
    });
    return await ds
        .upsert({
        objectType: BOARD_ENDPOINTS.VIEW_ZONES,
        payload: [
            {
                type: BoardObjectTypes.ZONE_VIEW,
                attributes: {
                    order: nextOrder,
                    zone_id: newId,
                    view_id: viewId,
                },
            },
        ],
    })
        .then((res) => {
        return {
            newZoneId: newId,
            newZoneViewId: res[0].id,
        };
    });
}
export async function addComponent(ds, objectType, title, nextOrder, componentType, widgetType, zoneId, dataUrl) {
    const user = getUserFromLocalStorage();
    const newId = generateId("c");
    await ds.upsert({
        objectType: BOARD_ENDPOINTS.COMPONENT,
        payload: [
            {
                type: BoardObjectTypes.COMPONENT,
                id: newId,
                attributes: {
                    title: title,
                    object_type: objectType,
                    component_type: componentType,
                    widget_type: widgetType,
                    filter: { and_: {} },
                    config: {},
                    base_url: dataUrl,
                    user_id: user.id,
                    filter_pass_through: false,
                },
            },
        ],
    });
    return await ds
        .upsert({
        objectType: BOARD_ENDPOINTS.ZONE_COMPONENTS,
        payload: [
            {
                type: BoardObjectTypes.COMPONENT_ZONE,
                attributes: {
                    order: nextOrder,
                    component_id: newId,
                    zone_id: zoneId,
                },
            },
        ],
    })
        .then((res) => {
        return {
            newComponentId: newId,
            newComponentZoneId: res[0].id,
        };
    });
}
export async function upsertComponent(ds, componentId, attributes) {
    return await ds.upsert({
        objectType: BOARD_ENDPOINTS.COMPONENT,
        payload: [
            {
                type: BoardObjectTypes.COMPONENT,
                id: componentId,
                attributes: attributes
            },
        ],
    });
}
export async function upsertComponentConfig(ds, componentId, config) {
    return await upsertComponent(ds, componentId, { config: config });
}
